function toPathname(urlish) {
  if (!urlish) return '';

  // Reserved domain used only for pathname parsing of relative URLs.
  const URL_BASE_FOR_PATH_RESOLUTION = 'https://dummy-url.com';
  try {
    return new URL(urlish, URL_BASE_FOR_PATH_RESOLUTION).pathname;
  } catch {
    return '';
  }
}

const PATH_ALIAS_RULES = [
  // /trainings (list) → /trainings/home (library entry point)
  {
    currentMatches: (currentPath) => /\/companies\/[^/]+\/trainings\/?$/.test(currentPath),
    targetMatches: (targetPath) => /\/companies\/[^/]+\/trainings\/home\/?$/.test(targetPath),
    score: 9_000,
  },
  // /journey_templates → /my_journeys
  {
    currentMatches: (currentPath) => currentPath.includes('/journey_templates'),
    targetMatches: (targetPath) => targetPath.includes('/my_journeys'),
    score: 9_000,
  },
  // /course_authoring/templates → /course_authoring/trainings
  {
    currentMatches: (currentPath) => currentPath.includes('/course_authoring/templates'),
    targetMatches: (targetPath) => targetPath.includes('/course_authoring/trainings'),
    score: 9_000,
  },
  // /phishing_simulator/onboarding → /phishing_simulator/campaigns
  {
    currentMatches: (currentPath) => currentPath.includes('/phishing_simulator/onboarding'),
    targetMatches: (targetPath) => targetPath.includes('/phishing_simulator/campaigns'),
    score: 9_000,
  },
  // /learner_documents → /policies (sidebar Documents menu uses /policies URL)
  {
    currentMatches: (currentPath) => /\/learners\/[^/]+\/learner_documents/.test(currentPath),
    targetMatches: (targetPath) => /\/learners\/[^/]+\/policies/.test(targetPath),
    score: 9_000,
  },
  // /groups → /learners (Groups sub-section maps to the Learners menu item)
  {
    currentMatches: (currentPath) => /\/companies\/[^/]+\/groups/.test(currentPath),
    targetMatches: (targetPath) => /\/companies\/[^/]+\/learners$/.test(targetPath),
    score: 9_000,
  },
  // Training detail/new pages → Training Library (/trainings/home)
  // Prefix match fails: /trainings/:id does not start with /trainings/home/
  {
    currentMatches: (currentPath) => /\/companies\/[^/]+\/trainings\/(?:\d+|new)/.test(currentPath),
    targetMatches: (targetPath) => /\/companies\/[^/]+\/trainings/.test(targetPath),
    score: 9_000,
  },
  // Journey detail/new pages → Learning Journeys (/my_journeys)
  // Needed for /journeys/:id routes where prefix match to /my_journeys fails
  {
    currentMatches: (currentPath) => /\/companies\/[^/]+\/(my_)?journeys\/(?:\d+|new)/.test(currentPath),
    targetMatches: (targetPath) => /\/companies\/[^/]+\/my_journeys/.test(targetPath),
    score: 9_000,
  },
  // Insights pages (classic ↔ smart dashboards) → Insights sidebar item
  // /insights and /insights/dashboards/:id are both "Insights" but don't share a prefix,
  // so switching between basic and advanced views would otherwise lose the active selection.
  {
    currentMatches: (currentPath) => /\/companies\/[^/]+\/insights/.test(currentPath),
    targetMatches: (targetPath) => /\/companies\/[^/]+\/insights/.test(targetPath),
    score: 9_000,
  },
];

function getPathAliasScore(currentPath, targetPath) {
  if (!currentPath || !targetPath) return -1;

  for (const rule of PATH_ALIAS_RULES) {
    if (rule.currentMatches(currentPath) && rule.targetMatches(targetPath)) {
      return rule.score;
    }
  }

  return -1;
}

/** Single source of truth for path matching priority:
 *  1. Exact match (score 10_000) — identical paths (trailing-slash agnostic)
 *  2. Alias match (score 9_000)   — e.g. /trainings → /trainings/home
 *  3. Prefix match (score 1_000)  — currentPath starts with targetPath/
 *  4. No match (score -1)
 *
 *  /companies is special: only exact match scores; prefix returns -1.
 */
function computePathMatchScore(currentPath, targetPath) {
  if (!currentPath || !targetPath) return -1;

  // Normalize: strip ALL trailing slashes (preserve root '/' for empty string)
  const normCurrent = currentPath.replace(/\/+$/, '') || '/';
  const normTarget = targetPath.replace(/\/+$/, '') || '/';

  // 1. Exact match (trailing-slash agnostic)
  if (normTarget === normCurrent) return 10_000 + normTarget.length;

  // 2. Alias match (existing rules already handle trailing slashes in regex)
  const aliasScore = getPathAliasScore(currentPath, targetPath);
  if (aliasScore >= 0) return aliasScore + normTarget.length;

  // 3. /companies special case: no prefix matches (covers both /companies and /companies/)
  if (normTarget === '/companies') return -1;

  // 4. Prefix match (trailing-slash agnostic)
  if (normCurrent.startsWith(`${normTarget}/`)) return 1_000 + normTarget.length;

  return -1;
}

function isPathActive(currentPath, href) {
  if (!currentPath || !href || href === '#') return false;
  return computePathMatchScore(currentPath, toPathname(href)) >= 0;
}

function findSelectedItemIdForPath(sections, pathname) {
  const walk = (item) => {
    let best = {
      id: null,
      score: computePathMatchScore(pathname, toPathname(item.path)),
    };
    if (best.score >= 0) best.id = item.id;

    for (const child of item.children || []) {
      const childBest = walk(child);
      // Prefer deeper (child) matches on ties so duplicate parent/child URLs
      // (e.g. Phishing Simulator -> Campaigns) select the leaf row.
      if (childBest.score >= best.score) best = childBest;
    }

    return best;
  };

  let best = { id: null, score: -1 };
  for (const section of sections) {
    for (const item of section.items || []) {
      const match = walk(item);
      if (match.score > best.score) best = match;
    }
  }
  return best.id || undefined;
}

export {
  toPathname,
  PATH_ALIAS_RULES,
  getPathAliasScore,
  computePathMatchScore,
  isPathActive,
  findSelectedItemIdForPath,
};
